/**
 * Шелл Mini App: фон, нижнее меню, переключение экранов.
 * Структура — design-spec §0; разделы — воронка BOT_SPEC (§4.4).
 */
import { useEffect, useState } from 'react';
import { api } from './lib/api.js';
import type { AppConfig } from './lib/api.js';
import { haptic } from './lib/telegram.js';
import { AppProvider, useApp } from './state/app.js';
import type { TabId } from './state/app.js';
import { HomeScreen } from './screens/HomeScreen.js';
import { TradeScreen } from './screens/TradeScreen.js';
import { TokensScreen } from './screens/TokensScreen.js';
import { AccountScreen } from './screens/AccountScreen.js';
import { AccountIcon, HomeIcon, NavIconDefs, TokensIcon, TradeIcon } from './components/NavIcons.js';

/** Иконки меню — инлайн-SVG (см. NavIcons.tsx): активная горит аврора-градиентом. */
const TABS: Array<{
  id: TabId;
  Icon: (props: { active: boolean }) => JSX.Element;
  labelKey: string;
}> = [
  { id: 'home', Icon: HomeIcon, labelKey: 'app.nav.home' },
  { id: 'trade', Icon: TradeIcon, labelKey: 'app.nav.trade' },
  { id: 'tokens', Icon: TokensIcon, labelKey: 'app.nav.tokens' },
  { id: 'account', Icon: AccountIcon, labelKey: 'app.nav.account' },
];

function Shell() {
  const { tab, setTab, loading, error, me, toast, t } = useApp();
  const [config, setConfig] = useState<AppConfig | null>(null);

  useEffect(() => {
    void api.config().then(setConfig).catch(() => undefined);
  }, []);

  if (loading) {
    return (
      <div className="app-boot">
        <div className="app-boot-spinner" />
        <span>{t('app.state.loading', {}, 'Loading…')}</span>
      </div>
    );
  }

  if (error || !me) {
    return (
      <div className="app-boot">
        <span>{error ?? t('app.state.no_data', {}, 'No data')}</span>
      </div>
    );
  }

  const brandName = config?.brand.name ?? '';
  const support = config?.brand.supportContact ?? '';

  return (
    <div className="app-shell">
      <main className="app-main">
        {tab === 'home' && <HomeScreen brandName={brandName} />}
        {tab === 'trade' && <TradeScreen config={config} />}
        {tab === 'tokens' && <TokensScreen supportContact={support} />}
        {tab === 'account' && <AccountScreen supportContact={support} config={config} />}
      </main>

      <nav className="bottom-nav">
        <NavIconDefs />
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`nav-item ${tab === item.id ? 'is-active' : ''}`}
            onClick={() => {
              haptic();
              setTab(item.id);
            }}
          >
            <span className="nav-icon">
              <item.Icon active={tab === item.id} />
            </span>
            <span className="nav-label">{t(item.labelKey)}</span>
          </button>
        ))}
      </nav>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

export function App() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
